import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  Menu,
  MenuItem,
  Button,
  Stack,
  Chip,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  LinearProgress,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  ListItemIcon,
  ListItemText,
  Alert,
  Divider,
  FormHelperText,
  Autocomplete,
  TextField
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import { useApi } from '../contexts/ApiContext';
import { useNotification } from '../contexts/NotificationContext';
import { usePermissions } from '../hooks/usePermissions';

const numberFormatter = new Intl.NumberFormat('es-CO');
const percentFormatter = new Intl.NumberFormat('es-CO', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const KPI_DEFINITIONS = [
  { key: 'registros', label: 'Registros activos' },
  { key: 'iniciales', label: 'Iniciales' },
  { key: 'gestionados', label: 'Gestionados' },
  { key: 'enrutados', label: 'Enrutados' },
  { key: 'programados', label: 'Programados' }
];

const TABLE_COLUMNS = [
  { key: 'skill', label: 'Skill', align: 'left' },
  { key: 'registros', label: 'Registros', align: 'right' },
  { key: 'iniciales', label: 'Iniciales', align: 'right' },
  { key: 'gestionados', label: 'Gestionados', align: 'right' },
  { key: 'porcentajeGestionado', label: '% Gestionado', align: 'right', isPercent: true },
  { key: 'enrutados', label: 'Enrutados', align: 'right' },
  { key: 'porcentajeEnrutado', label: '% Enrutado', align: 'right', isPercent: true },
  { key: 'programados', label: 'Programados', align: 'right' }
];

const formatNumber = (value = 0) => numberFormatter.format(value || 0);
const formatPercent = (value = 0) => `${percentFormatter.format(value || 0)} %`;

const MIN_REFRESH_INTERVAL = 15000;
const ANIMATION_DURATION_MS = 5000;
const ANIMATION_MIN_INTERVAL_MS = 50;
const ANIMATION_MAX_INTERVAL_MS = 250;

export default function SupervisorSkillsPage() {
  const { campaigns, supervisor, loads, dialer, sse } = useApi();
  const { showError, showSuccess } = useNotification();
  const { canAction } = usePermissions();

  const [campaignList, setCampaignList] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [skills, setSkills] = useState([]);
  const [totals, setTotals] = useState(null);
  const [displayedSkills, setDisplayedSkills] = useState([]);
  const [displayedTotals, setDisplayedTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [silentRefresh, setSilentRefresh] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [cachedResult, setCachedResult] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [expandedSkills, setExpandedSkills] = useState({});
  const [contextMenu, setContextMenu] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [resetDialog, setResetDialog] = useState({ open: false, mode: null, skill: null, load: null });
  const [selectedLoadIds, setSelectedLoadIds] = useState([]);
  const [resetAttempts, setResetAttempts] = useState(false);
  const [finalOptions, setFinalOptions] = useState([]);
  const [selectedFinals, setSelectedFinals] = useState([]);
  const [finalsLoading, setFinalsLoading] = useState(false);
  const [userRefreshInterval, setUserRefreshInterval] = useState(() => {
    const saved = localStorage.getItem('supervisorSkills_refreshInterval');
    return saved ? Number(saved) : MIN_REFRESH_INTERVAL / 1000;
  });
  const [hideInactiveSkills, setHideInactiveSkills] = useState(() => {
    const saved = localStorage.getItem('supervisorSkills_hideInactiveSkills');
    return saved === 'true';
  });
  const [hideInactiveLoads, setHideInactiveLoads] = useState(() => {
    const saved = localStorage.getItem('supervisorSkills_hideInactiveLoads');
    return saved === 'true';
  });

  const refreshIntervalMs = useMemo(() => Math.max(userRefreshInterval * 1000, MIN_REFRESH_INTERVAL), [userRefreshInterval]);
  const animationRef = useRef(null);
  const displayedSkillsRef = useRef([]);
  const displayedTotalsRef = useRef(null);

  const loadCampaigns = useCallback(async () => {
    try {
      const response = await campaigns.getAll();
      setCampaignList(response?.data?.campaigns || []);
    } catch (error) {
      console.error('[SupervisorSkills] Error cargando campañas', error);
      showError(`No se pudieron cargar las campañas: ${error.message}`);
    }
  }, [campaigns, showError]);

  const animateMetricsTransition = useCallback((nextSkills = [], nextTotals = null, skipAnimation = false) => {
    const metricKeys = ['registros', 'iniciales', 'gestionados', 'enrutados', 'programados'];
    const previousSkills = displayedSkillsRef.current;

    if (!previousSkills.length || skipAnimation) {
      setDisplayedSkills(nextSkills);
      setDisplayedTotals(nextTotals);
      return;
    }

    if (animationRef.current) {
      clearInterval(animationRef.current);
      animationRef.current = null;
    }

    const previousMap = new Map(previousSkills.map((skill) => [skill.skilldef, skill]));

    let workingSkills = nextSkills.map((skill) => {
      const clone = { ...skill };
      const previous = previousMap.get(skill.skilldef);
      metricKeys.forEach((key) => {
        clone[key] = previous ? previous[key] ?? 0 : clone[key] ?? 0;
      });
      return clone;
    });

    let workingTotals = nextTotals ? { ...nextTotals } : null;
    if (workingTotals && displayedTotalsRef.current) {
      metricKeys.forEach((key) => {
        workingTotals[key] = displayedTotalsRef.current[key] ?? 0;
      });
    }

    const calculateMaxDiff = () => {
      let max = 0;
      nextSkills.forEach((target, index) => {
        const current = workingSkills[index];
        if (!target || !current) return;
        metricKeys.forEach((key) => {
          const diff = Math.abs((target[key] ?? 0) - (current[key] ?? 0));
          if (diff > max) {
            max = diff;
          }
        });
      });
      if (workingTotals && nextTotals) {
        metricKeys.forEach((key) => {
          const diff = Math.abs((nextTotals[key] ?? 0) - (workingTotals[key] ?? 0));
          if (diff > max) {
            max = diff;
          }
        });
      }
      return max;
    };

    const maxDiff = calculateMaxDiff();
    if (!maxDiff) {
      setDisplayedSkills(nextSkills);
      setDisplayedTotals(nextTotals);
      return;
    }

    const effectiveDuration = ANIMATION_DURATION_MS;
    const baseDelay = effectiveDuration / Math.max(maxDiff, 1);
    const stepDelay = Math.max(
      ANIMATION_MIN_INTERVAL_MS,
      Math.min(ANIMATION_MAX_INTERVAL_MS, baseDelay)
    );
    const totalTicks = Math.max(Math.ceil(effectiveDuration / stepDelay), 1);

    const buildMetricState = (currentValue = 0, targetValue = 0) => {
      const diff = targetValue - currentValue;
      return {
        target: targetValue,
        direction: diff >= 0 ? 1 : -1,
        remaining: Math.abs(diff),
        accumulator: 0,
        step: Math.abs(diff) / totalTicks
      };
    };

    const skillAnimationState = workingSkills.map((current, index) => {
      const target = nextSkills[index];
      if (!target || !current) return null;
      const metricState = {};
      metricKeys.forEach((key) => {
        metricState[key] = buildMetricState(current[key] ?? 0, target[key] ?? 0);
      });
      return { metrics: metricState };
    });

    const totalsAnimationState = workingTotals && nextTotals
      ? metricKeys.reduce((acc, key) => {
        acc[key] = buildMetricState(workingTotals[key] ?? 0, nextTotals[key] ?? 0);
        return acc;
      }, {})
      : null;

    let ticksElapsed = 0;

    animationRef.current = setInterval(() => {
      ticksElapsed += 1;
      const forceCompletion = ticksElapsed >= totalTicks;
      let hasPending = false;
      let producedChange = false;

      const updatedSkills = workingSkills.map((current, index) => {
        const target = nextSkills[index];
        if (!target || !current) {
          return current;
        }

        const state = skillAnimationState[index];
        if (!state) {
          return target;
        }

        const updated = { ...current };
        metricKeys.forEach((key) => {
          const metricState = state.metrics[key];
          if (!metricState || metricState.remaining <= 0) {
            updated[key] = target[key] ?? 0;
            return;
          }

          metricState.accumulator += metricState.step || 0;
          const pendingSteps = forceCompletion
            ? metricState.remaining
            : Math.min(metricState.remaining, Math.floor(metricState.accumulator));
          if (pendingSteps > 0) {
            updated[key] += metricState.direction * pendingSteps;
            metricState.remaining -= pendingSteps;
            metricState.accumulator -= pendingSteps;
            producedChange = true;
          }

          if (metricState.remaining <= 0) {
            updated[key] = metricState.target;
          } else {
            hasPending = true;
          }
        });

        const registros = updated.registros || 0;
        const gestionados = updated.gestionados || 0;
        updated.porcentajeGestionado = registros > 0
          ? Math.round(((gestionados / registros) * 100) * 100) / 100
          : 0;
        updated.porcentajeEnrutado = gestionados > 0
          ? Math.round((((updated.enrutados || 0) / (gestionados || 1)) * 100) * 100) / 100
          : 0;

        return updated;
      });

      let updatedTotalsData = workingTotals;
      if (workingTotals && nextTotals && totalsAnimationState) {
        const updatedTotals = { ...workingTotals };
        metricKeys.forEach((key) => {
          const metricState = totalsAnimationState[key];
          if (!metricState || metricState.remaining <= 0) {
            updatedTotals[key] = nextTotals[key] ?? 0;
            return;
          }

          metricState.accumulator += metricState.step || 0;
          const pendingSteps = forceCompletion
            ? metricState.remaining
            : Math.min(metricState.remaining, Math.floor(metricState.accumulator));
          if (pendingSteps > 0) {
            updatedTotals[key] += metricState.direction * pendingSteps;
            metricState.remaining -= pendingSteps;
            metricState.accumulator -= pendingSteps;
            producedChange = true;
          }

          if (metricState.remaining <= 0) {
            updatedTotals[key] = metricState.target;
          } else {
            hasPending = true;
          }
        });
        updatedTotals.porcentajeGestionado = updatedTotals.registros > 0
          ? Math.round((((updatedTotals.gestionados || 0) / (updatedTotals.registros || 1)) * 100) * 100) / 100
          : 0;
        updatedTotals.porcentajeEnrutado = updatedTotals.gestionados > 0
          ? Math.round((((updatedTotals.enrutados || 0) / (updatedTotals.gestionados || 1)) * 100) * 100) / 100
          : 0;
        updatedTotalsData = updatedTotals;
      }

      workingSkills = updatedSkills;
      workingTotals = updatedTotalsData;

      if (producedChange) {
        setDisplayedSkills(updatedSkills);
        setDisplayedTotals(updatedTotalsData || nextTotals);
      }

      if (!hasPending) {
        clearInterval(animationRef.current);
        animationRef.current = null;
        setDisplayedSkills(nextSkills);
        setDisplayedTotals(nextTotals);
      }
    }, stepDelay);
  }, []);

  const handleMetricsPayload = useCallback((payload = {}, skipAnimation = false) => {
    const skillsData = payload.skills || [];
    const totalsData = payload.totals || null;

    setSkills(skillsData);
    setTotals(totalsData);
    setLastUpdated(payload.generatedAt || new Date().toISOString());
    setCachedResult(Boolean(payload.cached));
    animateMetricsTransition(skillsData, totalsData, skipAnimation);
  }, [animateMetricsTransition]);

  const fetchMetrics = useCallback(async ({ showSpinner = true, forceRefresh = false, skipAnimation = false } = {}) => {
    if (showSpinner) {
      setLoading(true);
    } else {
      setSilentRefresh(true);
    }

    try {
      const params = {};
      if (selectedCampaign) {
        params.campaignId = selectedCampaign;
      }
      if (forceRefresh) {
        params.forceRefresh = true;
      }

      const response = await supervisor.getSkillMetrics(params);
      handleMetricsPayload(response?.data || {}, skipAnimation);
    } catch (error) {
      console.error('[SupervisorSkills] Error cargando métricas', error);
      showError(`No se pudieron cargar las métricas: ${error.message}`);
    } finally {
      if (showSpinner) {
        setLoading(false);
      } else {
        setSilentRefresh(false);
      }
    }
  }, [handleMetricsPayload, selectedCampaign, showError, supervisor]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  useEffect(() => {
    localStorage.setItem('supervisorSkills_refreshInterval', String(userRefreshInterval));
  }, [userRefreshInterval]);

  useEffect(() => {
    localStorage.setItem('supervisorSkills_hideInactiveSkills', String(hideInactiveSkills));
  }, [hideInactiveSkills]);

  useEffect(() => {
    localStorage.setItem('supervisorSkills_hideInactiveLoads', String(hideInactiveLoads));
  }, [hideInactiveLoads]);

  useEffect(() => {
    displayedSkillsRef.current = displayedSkills;
  }, [displayedSkills]);

  useEffect(() => {
    displayedTotalsRef.current = displayedTotals;
  }, [displayedTotals]);

  useEffect(() => () => {
    if (animationRef.current) {
      clearInterval(animationRef.current);
      animationRef.current = null;
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  useEffect(() => {
    if (!sse || !autoRefresh) {
      return undefined;
    }

    const normalizedSelected = selectedCampaign ? String(selectedCampaign) : null;

    const handler = (event) => {
      try {
        const payload = JSON.parse(event.data || '{}');
        const eventCampaignId = payload.campaignId === undefined || payload.campaignId === null
          ? null
          : String(payload.campaignId);

        if ((eventCampaignId || null) !== (normalizedSelected || null)) {
          return;
        }

        handleMetricsPayload(payload);
      } catch (error) {
        console.error('[SupervisorSkills] Error processing SSE metrics event', error);
      }
    };

    sse.addEventListener('skill-metrics', handler);

    return () => {
      sse.removeEventListener('skill-metrics', handler);
    };
  }, [autoRefresh, handleMetricsPayload, selectedCampaign, sse]);

  useEffect(() => {
    if (autoRefresh && sse) {
      return undefined;
    }

    const interval = setInterval(() => {
      fetchMetrics({ showSpinner: false });
    }, refreshIntervalMs);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshIntervalMs, fetchMetrics, sse]);

  useEffect(() => {
    if (!resetDialog.open) {
      setSelectedLoadIds([]);
      setSelectedFinals([]);
      setFinalOptions([]);
      setResetAttempts(false);
      setFinalsLoading(false);
    }
  }, [resetDialog.open]);

  useEffect(() => {
    if (!resetDialog.open || !resetDialog.skill || selectedLoadIds.length === 0) {
      if (!resetDialog.open) {
        setFinalOptions([]);
        setSelectedFinals([]);
      }
      setFinalsLoading(false);
      return;
    }

    let active = true;
    setFinalsLoading(true);
    supervisor
      .getEndGroups(resetDialog.skill.skilldef, selectedLoadIds)
      .then((response) => {
        if (!active) return;
        const groups = response?.data?.groups || [];
        setFinalOptions(groups);
        setSelectedFinals([]);
      })
      .catch((error) => {
        if (!active) return;
        console.error('[SupervisorSkills] Error cargando finales', error);
        showError(error.message || 'No se pudieron cargar los finales');
        setFinalOptions([]);
        setSelectedFinals([]);
      })
      .finally(() => {
        if (active) setFinalsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [resetDialog.open, resetDialog.skill, selectedLoadIds, supervisor, showError]);

  const handleCampaignChange = (event) => {
    setSelectedCampaign(event.target.value);
  };

  const handleManualRefresh = () => {
    if (autoRefresh && sse) {
      supervisor
        .refreshSkillMetrics(selectedCampaign || null)
        .catch((error) => {
          console.error('[SupervisorSkills] Error forcing metrics refresh', error);
          showError(`Error al refrescar métricas: ${error.message}`);
          fetchMetrics({ showSpinner: false, forceRefresh: true, skipAnimation: true });
        });
      return;
    }
    fetchMetrics({ showSpinner: false, forceRefresh: true, skipAnimation: true });
  };

  const toggleSkillRow = (key) => {
    if (!key) return;
    setExpandedSkills((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const availableLoads = useMemo(() => {
    if (!resetDialog.skill || !Array.isArray(resetDialog.skill.loads)) {
      return [];
    }
    return resetDialog.skill.loads
      .filter((load) => load.idload !== null && load.idload !== undefined && !Number.isNaN(Number(load.idload)))
      .map((load) => ({
        ...load,
        idload: Number(load.idload)
      }));
  }, [resetDialog.skill]);

  const handleSkillContextMenu = useCallback((event, skillRow) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
      type: 'skill',
      skill: skillRow
    });
  }, []);

  const handleLoadContextMenu = useCallback((event, skillRow, loadRow) => {
    event.preventDefault();
    event.stopPropagation();
    if (loadRow?.idload === null || loadRow?.idload === undefined) {
      return;
    }
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
      type: 'load',
      skill: skillRow,
      load: loadRow
    });
  }, []);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleStopSkill = useCallback(async () => {
    if (!contextMenu?.skill) return;
    try {
      setActionLoading(true);
      await dialer.stopSkill(contextMenu.skill.skilldef);
      showSuccess(`Skill ${contextMenu.skill.skillName} detenido`);
      fetchMetrics({ showSpinner: false, skipAnimation: true });
    } catch (error) {
      console.error('[SupervisorSkills] Error deteniendo skill', error);
      showError(error.message || 'No se pudo detener el skill');
    } finally {
      setActionLoading(false);
      handleCloseContextMenu();
    }
  }, [contextMenu, dialer, fetchMetrics, showError, showSuccess, handleCloseContextMenu]);

  const handleStopLoad = useCallback(async () => {
    if (!contextMenu?.load?.idload && contextMenu?.load?.idload !== 0) return;
    const loadId = Number(contextMenu.load.idload);
    if (Number.isNaN(loadId)) return;
    try {
      setActionLoading(true);
      await loads.setActivationState(loadId, false);
      showSuccess(`Carga ${contextMenu.load.loadName || contextMenu.load.idload} detenida`);
      fetchMetrics({ showSpinner: false, skipAnimation: true });
    } catch (error) {
      console.error('[SupervisorSkills] Error deteniendo carga', error);
      showError(error.message || 'No se pudo detener la carga');
    } finally {
      setActionLoading(false);
      handleCloseContextMenu();
    }
  }, [contextMenu, loads, fetchMetrics, showError, showSuccess, handleCloseContextMenu]);

  const openResetDialog = useCallback((mode, skillRow, loadRow = null) => {
    setResetDialog({
      open: true,
      mode,
      skill: skillRow,
      load: loadRow
    });
    if (mode === 'load' && loadRow?.idload) {
      setSelectedLoadIds([Number(loadRow.idload)]);
    } else {
      setSelectedLoadIds([]);
    }
    setSelectedFinals([]);
    setResetAttempts(false);
    setFinalOptions([]);
  }, []);

  const handleOpenResetFromMenu = useCallback(() => {
    if (!contextMenu?.skill) return;
    if (contextMenu.type === 'load' && contextMenu.load?.idload) {
      openResetDialog('load', contextMenu.skill, contextMenu.load);
    } else {
      openResetDialog('skill', contextMenu.skill);
    }
    handleCloseContextMenu();
  }, [contextMenu, openResetDialog, handleCloseContextMenu]);

  const handleLoadSelectionChange = (event) => {
    const value = event.target.value;
    const normalized = Array.isArray(value)
      ? value.map((item) => Number(item))
      : [];
    setSelectedLoadIds(normalized);
  };


  const handleResetDialogClose = () => {
    setResetDialog({ open: false, mode: null, skill: null, load: null });
  };

  const handleResetSubmit = async () => {
    if (!resetDialog.skill || selectedLoadIds.length === 0 || selectedFinals.length === 0) {
      showError('Selecciona al menos una carga y un final a reiniciar');
      return;
    }
    const warningMessage = `Se reiniciarán ${selectedFinals.length} finales en ${selectedLoadIds.length} carga(s). Esta acción no se puede deshacer.`;
    if (!window.confirm(warningMessage)) {
      return;
    }
    try {
      setActionLoading(true);
      await supervisor.resetSkillRecords(resetDialog.skill.skilldef, {
        loadIds: selectedLoadIds,
        endGroups: selectedFinals,
        resetAttempts
      });
      showSuccess('Registros reiniciados correctamente');
      handleResetDialogClose();
      fetchMetrics({ showSpinner: false, skipAnimation: true });
    } catch (error) {
      console.error('[SupervisorSkills] Error reiniciando registros', error);
      showError(error.message || 'No se pudieron reiniciar los registros');
    } finally {
      setActionLoading(false);
    }
  };

  const renderSkillCell = (row) => (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="subtitle2">{row.skillName}</Typography>
      {row.missingTable && (
        <Tooltip title="La tabla del skill no existe. No se mostrarán datos hasta que se genere.">
          <WarningAmberIcon color="warning" fontSize="small" />
        </Tooltip>
      )}
    </Stack>
  );

  const filteredSkills = useMemo(() => {
    const baseSkills = displayedSkills.length > 0 ? displayedSkills : skills;

    return baseSkills.map((skill) => {
      // Filtrar cargas si está activo el filtro de cargas inactivas
      const filteredLoads = hideInactiveLoads && Array.isArray(skill.loads)
        ? skill.loads.filter((load) => load.active !== false)
        : skill.loads;

      return {
        ...skill,
        loads: filteredLoads
      };
    }).filter((skill) => {
      // Filtrar skills si está activo el filtro de skills inactivos
      if (hideInactiveSkills) {
        return skill.active !== false;
      }
      return true;
    });
  }, [displayedSkills, skills, hideInactiveSkills, hideInactiveLoads]);

  const skillRows = filteredSkills;
  const totalsForDisplay = displayedTotals || totals || null;
  const noData = !loading && skillRows.length === 0;

  return (
    <Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="campaign-filter-label">Campaña</InputLabel>
                <Select
                  labelId="campaign-filter-label"
                  value={selectedCampaign}
                  label="Campaña"
                  onChange={handleCampaignChange}
                >
                  <MenuItem value="">Todas las campañas</MenuItem>
                  {campaignList.map((campaign) => (
                    <MenuItem key={campaign.idcampaign} value={campaign.idcampaign}>
                      {campaign.descripcion}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Actualizado:
                  </Typography>
                  <Typography variant="body2">
                    {lastUpdated ? new Date(lastUpdated).toLocaleString() : 'Sin datos'}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    size="small"
                    label={cachedResult ? 'Desde caché' : 'Refresco reciente'}
                    color={cachedResult ? 'default' : 'success'}
                    variant="outlined"
                  />
                  <TextField
                    size="small"
                    type="number"
                    label="Intervalo (seg)"
                    value={userRefreshInterval}
                    onChange={(e) => {
                      const value = Math.max(MIN_REFRESH_INTERVAL / 1000, Number(e.target.value) || MIN_REFRESH_INTERVAL / 1000);
                      setUserRefreshInterval(value);
                    }}
                    inputProps={{ min: MIN_REFRESH_INTERVAL / 1000, step: 5 }}
                    sx={{ width: 140 }}
                  />
                </Stack>
              </Stack>
            </Grid>

            <Grid item xs={12} md={4}>
              <Stack spacing={1}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={autoRefresh}
                        onChange={(event) => setAutoRefresh(event.target.checked)}
                        color="primary"
                      />
                    }
                    label="Auto refrescar"
                  />
                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={handleManualRefresh}
                    disabled={loading || silentRefresh}
                  >
                    Actualizar
                  </Button>
                </Stack>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={hideInactiveSkills}
                        onChange={(event) => setHideInactiveSkills(event.target.checked)}
                        size="small"
                      />
                    }
                    label="Ocultar skills inactivos"
                    componentsProps={{ typography: { variant: 'body2' } }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={hideInactiveLoads}
                        onChange={(event) => setHideInactiveLoads(event.target.checked)}
                        size="small"
                      />
                    }
                    label="Ocultar cargas inactivas"
                    componentsProps={{ typography: { variant: 'body2' } }}
                  />
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
        <Box sx={{ height: 4 }}>
          {(loading || silentRefresh) && <LinearProgress />}
        </Box>
      </Card>

      {totalsForDisplay && (
        <Grid container spacing={2} mb={3}>
          {KPI_DEFINITIONS.map((kpi) => (
            <Grid item xs={12} sm={6} md={3} key={kpi.key}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    {kpi.label}
                  </Typography>
                  <Typography variant="h5" fontWeight={600}>
                    {formatNumber(totalsForDisplay[kpi.key] || 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  % Gestionado
                </Typography>
                <Typography variant="h5" fontWeight={600}>
                  {formatPercent(totalsForDisplay.porcentajeGestionado || 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  % Enrutado
                </Typography>
                <Typography variant="h5" fontWeight={600}>
                  {formatPercent(totalsForDisplay.porcentajeEnrutado || 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {noData ? (
          <Box py={6} textAlign="center">
            <Typography variant="subtitle1" fontWeight={500}>
              No hay skills para mostrar
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Verifica que la campaña seleccionada tenga skills activos.
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                {TABLE_COLUMNS.map((column) => (
                  <TableCell key={column.key} align={column.align}>
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {skillRows.map((row) => {
                const skillKey = row.skilldef || row.id;
                const isExpanded = Boolean(expandedSkills[skillKey]);
                const loadList = Array.isArray(row.loads) ? row.loads : [];
                return (
                  <React.Fragment key={`${row.skilldef}-${row.id}`}>
                    <TableRow
                      hover
                      onClick={() => toggleSkillRow(skillKey)}
                      onContextMenu={(event) => handleSkillContextMenu(event, row)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{renderSkillCell(row)}</TableCell>
                      <TableCell align="right">{formatNumber(row.registros)}</TableCell>
                      <TableCell align="right">{formatNumber(row.iniciales)}</TableCell>
                      <TableCell align="right">{formatNumber(row.gestionados)}</TableCell>
                      <TableCell align="right">{formatPercent(row.porcentajeGestionado)}</TableCell>
                      <TableCell align="right">{formatNumber(row.enrutados)}</TableCell>
                      <TableCell align="right">{formatPercent(row.porcentajeEnrutado)}</TableCell>
                      <TableCell align="right">{formatNumber(row.programados)}</TableCell>
                    </TableRow>
                    {isExpanded && (loadList.length ? (
                      loadList.map((load) => {
                        const loadIdNumber = Number(load.idload);
                        const hasLoadId = !Number.isNaN(loadIdNumber);
                        return (
                          <TableRow
                            key={`${row.skilldef}-${hasLoadId ? loadIdNumber : `nl-${load.loadName || 'sin'}`}`}
                            onContextMenu={(event) => {
                              if (!hasLoadId) return;
                              handleLoadContextMenu(event, row, load);
                            }}
                            sx={{
                              backgroundColor: (theme) => theme.palette.action.hover,
                              cursor: hasLoadId ? 'context-menu' : 'default'
                            }}
                          >
                            <TableCell sx={{ pl: 6 }}>
                              <Typography variant="subtitle2">{load.loadName || 'Sin nombre'}</Typography>
                              {hasLoadId && (
                                <Typography variant="caption" color="text.secondary">
                                  ID: {loadIdNumber}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">{formatNumber(load.registros)}</TableCell>
                            <TableCell align="right">{formatNumber(load.iniciales)}</TableCell>
                            <TableCell align="right">{formatNumber(load.gestionados)}</TableCell>
                            <TableCell align="right">{formatPercent(load.porcentajeGestionado)}</TableCell>
                            <TableCell align="right">{formatNumber(load.enrutados)}</TableCell>
                            <TableCell align="right">{formatPercent(load.porcentajeEnrutado)}</TableCell>
                            <TableCell align="right">{formatNumber(load.programados)}</TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow
                        key={`${row.skilldef}-empty`}
                        sx={{ backgroundColor: (theme) => theme.palette.action.hover }}
                      >
                        <TableCell colSpan={TABLE_COLUMNS.length} sx={{ pl: 6 }}>
                          <Typography variant="body2" color="text.secondary">
                            Sin información de cargas asociadas.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                );
              })}
              {totalsForDisplay && (
                <TableRow sx={{ backgroundColor: (theme) => theme.palette.action.hover }}>
                  <TableCell>
                    <Typography variant="subtitle2">Totales</Typography>
                  </TableCell>
                  <TableCell align="right">{formatNumber(totalsForDisplay.registros)}</TableCell>
                  <TableCell align="right">{formatNumber(totalsForDisplay.iniciales)}</TableCell>
                  <TableCell align="right">{formatNumber(totalsForDisplay.gestionados)}</TableCell>
                  <TableCell align="right">{formatPercent(totalsForDisplay.porcentajeGestionado)}</TableCell>
                  <TableCell align="right">{formatNumber(totalsForDisplay.enrutados)}</TableCell>
                  <TableCell align="right">{formatPercent(totalsForDisplay.porcentajeEnrutado)}</TableCell>
                  <TableCell align="right">{formatNumber(totalsForDisplay.programados)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Menu
        open={Boolean(contextMenu)}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        keepMounted
      >
        {contextMenu?.type === 'skill' && (
          <>
            {canAction('stop_skill_supervisor') && (
              <MenuItem onClick={handleStopSkill} disabled={actionLoading}>
                <ListItemIcon>
                  <StopCircleIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText
                  primary="Detener skill"
                  secondary={contextMenu.skill?.skillName}
                />
              </MenuItem>
            )}
            {canAction('reset_skill_supervisor') && (
              <MenuItem
                onClick={handleOpenResetFromMenu}
                disabled={
                  actionLoading ||
                  !((contextMenu.skill?.loads || []).some(
                    (load) => load.idload !== null && load.idload !== undefined
                  ))
                }
              >
                <ListItemIcon>
                  <RestartAltIcon fontSize="small" color="warning" />
                </ListItemIcon>
                <ListItemText primary="Pasar a iniciales" />
              </MenuItem>
            )}
          </>
        )}
        {contextMenu?.type === 'load' && (
          <>
            {canAction('stop_skill_supervisor') && (
              <MenuItem onClick={handleStopLoad} disabled={actionLoading}>
                <ListItemIcon>
                  <StopCircleIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText
                  primary="Detener carga"
                  secondary={contextMenu.load?.loadName || `ID ${contextMenu.load?.idload}`}
                />
              </MenuItem>
            )}
            {canAction('reset_skill_supervisor') && (
              <MenuItem
                onClick={handleOpenResetFromMenu}
                disabled={
                  actionLoading ||
                  contextMenu.load?.idload === null ||
                  contextMenu.load?.idload === undefined
                }
              >
                <ListItemIcon>
                  <RestartAltIcon fontSize="small" color="warning" />
                </ListItemIcon>
                <ListItemText primary="Pasar a iniciales" />
              </MenuItem>
            )}
          </>
        )}
      </Menu>

      <Dialog
        open={resetDialog.open}
        onClose={handleResetDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Pasar a iniciales - {resetDialog.skill?.skillName || 'Skill'}
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta acción reinicia los registros seleccionados como si fueran nuevos. Una vez aplicada,
            los datos no se pueden recuperar.
          </Alert>

          {resetDialog.mode === 'skill' ? (
            <FormControl fullWidth margin="normal">
              <InputLabel id="reset-loads-label">Cargas</InputLabel>
              <Select
                labelId="reset-loads-label"
                multiple
                value={selectedLoadIds}
                label="Cargas"
                onChange={handleLoadSelectionChange}
                renderValue={(selected) =>
                  selected
                    .map((id) => {
                      const load = availableLoads.find((item) => item.idload === id);
                      return load?.loadName || `Carga ${id}`;
                    })
                    .join(', ')
                }
              >
                {availableLoads.length === 0 && (
                  <MenuItem disabled>
                    <ListItemText primary="Sin cargas disponibles" />
                  </MenuItem>
                )}
                {availableLoads.map((load) => (
                  <MenuItem key={load.idload} value={load.idload}>
                    <Checkbox checked={selectedLoadIds.indexOf(load.idload) > -1} />
                    <ListItemText
                      primary={load.loadName || `Carga ${load.idload}`}
                      secondary={`ID: ${load.idload}`}
                    />
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Selecciona las cargas que deseas reiniciar
              </FormHelperText>
            </FormControl>
          ) : (
            <Box mb={2}>
              <Typography variant="subtitle2">Carga seleccionada</Typography>
              <Typography variant="body2">
                {resetDialog.load?.loadName || 'Sin nombre'} (ID:{' '}
                {resetDialog.load?.idload ?? 'N/A'})
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Autocomplete
            multiple
            fullWidth
            disabled={selectedLoadIds.length === 0 || finalsLoading}
            options={finalOptions}
            value={finalOptions.filter((option) => selectedFinals.indexOf(option.value) > -1)}
            onChange={(_, newValue) => {
              setSelectedFinals(newValue.map((option) => option.value));
            }}
            getOptionLabel={(option) => option.label || option.value}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Finales"
                placeholder={finalsLoading ? "Cargando finales..." : "Buscar finales..."}
                helperText="Selecciona los finales que se reiniciarán"
                margin="normal"
              />
            )}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <li key={key} {...optionProps}>
                  <Checkbox
                    checked={selectedFinals.indexOf(option.value) > -1}
                    sx={{ mr: 1 }}
                  />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2">
                      {option.label || option.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.total} registro(s)
                    </Typography>
                  </Box>
                </li>
              );
            }}
            noOptionsText={finalsLoading ? "Cargando finales..." : "No hay finales disponibles"}
            sx={{ mt: 2 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={resetAttempts}
                onChange={(event) => setResetAttempts(event.target.checked)}
              />
            }
            label="Reiniciar intentos"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResetDialogClose} disabled={actionLoading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleResetSubmit}
            disabled={
              actionLoading ||
              finalsLoading ||
              selectedLoadIds.length === 0 ||
              selectedFinals.length === 0
            }
          >
            Reiniciar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
